import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { BookOpen, ArrowRight, Award } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import { cn } from '@/lib/utils';

interface EnrolledCourse {
    id: string;
    title: string;
    slug: string;
    description: string;
    thumbnail: string;
    progress: number;
}

const MyCourses = () => {
    const { user } = useAuth();
    const [courses, setCourses] = useState<EnrolledCourse[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchEnrolledCourses = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                // Fetch active enrollments with course details join
                const { data, error } = await supabase
                    .from('enrollments')
                    .select(`
                        id,
                        status,
                        progress,
                        courses (
                            id,
                            title,
                            slug,
                            description,
                            thumbnail
                        )
                    `)
                    .eq('user_id', user.id)
                    .eq('status', 'active');

                if (error) throw error;

                const enrolled = (data || []).map((en: any) => {
                    const course = en.courses;
                    if (!course) return null;

                    return {
                        id: course.id,
                        title: course.title,
                        slug: course.slug,
                        description: course.description,
                        thumbnail: course.thumbnail,
                        progress: en.progress || 0
                    };
                }).filter(Boolean) as EnrolledCourse[];

                setCourses(enrolled);
            } catch (error) {
                console.error('Learning Vault Sync Error:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEnrolledCourses();
    }, [user]);

    if (isLoading) {
        return (
            <Layout>
                <div className="section-padding container-max">
                    <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-20 animate-pulse">
                        <div className="max-w-2xl space-y-4">
                            <div className="h-6 w-32 bg-gray-100 rounded-full" />
                            <div className="h-12 w-64 bg-gray-100 rounded-2xl" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-[450px] bg-gray-50/50 border border-gray-100 rounded-[2.5rem] animate-pulse" />
                        ))}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <section className="bg-background min-h-[80vh] relative overflow-hidden py-8 md:py-12">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] z-0" />

                <div className="container-max relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-20">
                        <div className="max-w-2xl">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em]">
                                    My Courses
                                </span>
                                <div className="h-px w-12 bg-gray-100" />
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                                    {courses.length} ACTIVE TERMINALS
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black text-primary mb-6 tracking-tighter uppercase">Learning <span className="text-gradient-gold italic">Vault</span></h1>
                            <p className="text-xl text-muted-foreground font-medium leading-relaxed max-w-lg">Manage your courses and track your mastery progress.</p>
                        </div>
                        <div className="flex flex-col gap-4">
                            <Link to="/courses">
                                <Button className="btn-gold h-16 px-10 text-[10px] font-black uppercase tracking-[0.2em] shadow-premium">
                                    Explore More Paths
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {courses.length === 0 ? (
                        <div className="text-center py-32 bg-white rounded-[3rem] border border-gray-100 shadow-premium relative group overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                            <div className="relative z-10">
                                <div className="w-24 h-24 rounded-[2rem] bg-primary/5 flex items-center justify-center mx-auto mb-10 border border-primary/10 shadow-inner group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-700">
                                    <BookOpen className="w-10 h-10" />
                                </div>
                                <h3 className="text-3xl font-black text-primary uppercase tracking-tighter mb-4">No Active Enrollments</h3>
                                <p className="text-muted-foreground font-medium mb-12 max-w-md mx-auto leading-relaxed text-lg">You haven't initiated any professional paths yet. Take the first step today.</p>
                                <Link to="/courses">
                                    <Button className="btn-navy h-16 px-14 text-xs font-black uppercase tracking-widest shadow-premium">View Selection</Button>
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                            {courses.map((course) => (
                                <div key={course.id} className="bg-white rounded-[2.5rem] border border-gray-100 flex flex-col h-full overflow-hidden transition-all duration-500 shadow-sm hover:shadow-xl hover:-translate-y-1">
                                    <div className="aspect-[16/11] relative overflow-hidden p-6">
                                        <div className="w-full h-full overflow-hidden rounded-[2rem] relative">
                                            <img
                                                src={course.thumbnail}
                                                alt={course.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute top-4 right-4 px-4 py-2 bg-[#001B3D] rounded-full text-[8px] font-black uppercase tracking-widest text-white shadow-lg">
                                                ACTIVE COURSE
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-10 pt-2 flex flex-col flex-1">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                                            <span className="text-[9px] font-black text-[#D4AF37] uppercase tracking-widest">COURSE TERMINAL</span>
                                        </div>

                                        <h3 className={cn(
                                            "text-2xl font-black mb-3 uppercase tracking-tighter leading-tight line-clamp-2",
                                            course.title.toLowerCase().includes('starter') ? 'text-[#D4AF37]' : 'text-[#001B3D]'
                                        )}>
                                            {course.title}
                                        </h3>

                                        <p className="text-xs text-muted-foreground line-clamp-3 mb-6 leading-relaxed">
                                            {course.description}
                                        </p>

                                        <div className="mt-auto space-y-8">
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-end">
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">PROGRESS</span>
                                                    <span className="text-[9px] font-black text-slate-900">{course.progress}%</span>
                                                </div>
                                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-slate-200 rounded-full transition-all duration-1000 ease-out"
                                                        style={{ width: `${course.progress}%` }}
                                                    />
                                                </div>
                                            </div>

                                            <Link to={`/my-courses/${course.slug || course.id}`} className="block w-full">
                                                <Button className="w-full h-14 bg-[#001B3D] hover:bg-[#002654] text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl flex items-center justify-center gap-3">
                                                    OPEN COURSE
                                                    <ArrowRight className="w-4 h-4" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Transaction History Section */}
                    <div className="mt-32">
                        <div className="flex items-center gap-3 mb-12">
                            <span className="inline-block px-4 py-2 bg-primary/5 border border-primary/20 rounded-full text-primary text-[10px] font-black uppercase tracking-[0.2em]">
                                Transaction History
                            </span>
                            <div className="h-px flex-1 bg-gray-100" />
                        </div>

                        <div className="bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50/50">
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-gray-100">Course / Plan</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-gray-100">Amount Paid</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-gray-100">Payment ID</th>
                                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-gray-100">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        <PaymentRows userId={user?.id} />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

// Helper component for payment rows to keep code clean
const PaymentRows = ({ userId }: { userId?: string }) => {
    const [payments, setPayments] = useState<any[]>([]);

    useEffect(() => {
        if (!userId) return;
        const fetchPayments = async () => {
            const { data } = await supabase
                .from('payments')
                .select('*, courses(title)')
                .eq('user_id', userId)
                .eq('status', 'success')
                .order('paid_at', { ascending: false });
            setPayments(data || []);
        };
        fetchPayments();
    }, [userId]);

    if (payments.length === 0) {
        return (
            <tr>
                <td colSpan={4} className="px-8 py-12 text-center text-sm font-medium text-muted-foreground">
                    No payment records found.
                </td>
            </tr>
        );
    }

    return (
        <>
            {payments.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50/30 transition-colors">
                    <td className="px-8 py-6">
                        <span className="text-sm font-bold text-primary uppercase">{p.courses?.title || p.plan_key || 'Course Access'}</span>
                    </td>
                    <td className="px-8 py-6">
                        <span className="text-sm font-bold text-primary uppercase">₹{p.amount?.toLocaleString()}</span>
                    </td>
                    <td className="px-8 py-6">
                        <code className="text-[10px] font-mono bg-gray-100 px-2 py-1 rounded text-muted-foreground uppercase">{p.payment_id}</code>
                    </td>
                    <td className="px-8 py-6">
                        <span className="text-sm font-medium text-muted-foreground">
                            {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                        </span>
                    </td>
                </tr>
            ))}
        </>
    );
};

export default MyCourses;
